import { useEffect, useState, useMemo } from "react";
import { api, pct, statusBadge, rollingMonthLabels } from "../api";
import EmployeeProjectsModal from "../components/EmployeeProjectsModal";
import ExportBar from "../components/ExportBar";

export default function Employees() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState({ name: "", portfolio: "", cc: "", status: "" });
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [m1, m2, m3] = rollingMonthLabels(3);

  useEffect(() => { api.get("/data/latest").then((r) => setData(r.data)); }, []);

  const rows = useMemo(() => {
    if (!data?.employees) return [];
    return data.employees.filter((e) => {
      if (filter.name && !e.name.toLowerCase().includes(filter.name.toLowerCase())) return false;
      if (filter.portfolio && !(e.portfolio || "").toLowerCase().includes(filter.portfolio.toLowerCase())) return false;
      if (filter.cc && !(e.cc || "").toLowerCase().includes(filter.cc.toLowerCase())) return false;
      if (filter.status && e.status !== filter.status) return false;
      return true;
    }).sort((a, b) => b.avg - a.avg);
  }, [data, filter]);

  if (!data) return <div className="text-slate-500">Loading…</div>;
  if (data.empty) return <div className="bg-white p-8 rounded-xl text-center text-slate-500">No data. Upload an Excel first.</div>;

  return (
    <div data-testid="employees-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-xl font-bold" style={{ color: "#1a3666" }}>Employees ({rows.length} of {data.employees.length})</h2>
        <ExportBar spec={() => ({
          title: "Employees",
          filters: filter,
          columns: [
            { key: "name", label: "Employee" },
            { key: "cc", label: "CC" },
            { key: "portfolio", label: "Portfolio" },
            { key: "role", label: "Role", get: (r) => r.role || "—" },
            { key: "apr", label: `${m1} %`, get: (r) => pct(r.apr) },
            { key: "may", label: `${m2} %`, get: (r) => pct(r.may) },
            { key: "jun", label: `${m3} %`, get: (r) => pct(r.jun) },
            { key: "avg", label: "Avg %", get: (r) => pct(r.avg) },
            { key: "status", label: "Status", isStatus: true, get: (r) => statusBadge(r.status).label },
          ],
          rows,
          filename: "vSDI_Employees",
        })} />
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">CC</th>
                <th className="px-3 py-2 text-left">Portfolio</th>
                <th className="px-3 py-2 text-left">Role</th>
                <th className="px-3 py-2 text-center">{m1}</th>
                <th className="px-3 py-2 text-center">{m2}</th>
                <th className="px-3 py-2 text-center">{m3}</th>
                <th className="px-3 py-2 text-center">Avg</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
              <tr className="bg-slate-100">
                <th className="px-2 py-1"><input data-testid="filter-name" placeholder="filter…" value={filter.name}
                  onChange={(e) => setFilter((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-1.5 py-1 text-xs border rounded" /></th>
                <th className="px-2 py-1"><input placeholder="filter…" value={filter.cc}
                  onChange={(e) => setFilter((f) => ({ ...f, cc: e.target.value }))}
                  className="w-full px-1.5 py-1 text-xs border rounded" /></th>
                <th className="px-2 py-1"><input placeholder="filter…" value={filter.portfolio}
                  onChange={(e) => setFilter((f) => ({ ...f, portfolio: e.target.value }))}
                  className="w-full px-1.5 py-1 text-xs border rounded" /></th>
                <th colSpan={4}></th>
                <th colSpan={2}>
                  <select value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
                          className="w-full px-1.5 py-1 text-xs border rounded">
                    <option value="">All</option>
                    <option value="overload">Overloaded</option>
                    <option value="underload">Underloaded</option>
                    <option value="optimal">Optimal</option>
                    <option value="unassigned">Not Assigned</option>
                  </select>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const { label, color } = statusBadge(e.status);
                return (
                  <tr key={`${e.name}-${e.cc}`}
                      onClick={() => setSelectedEmp(e)}
                      className="border-t border-slate-100 hover:bg-blue-50 cursor-pointer transition"
                      data-testid="employee-row"
                      title="Click to see project allocations">
                    <td className="px-3 py-2 font-medium">{e.name}</td>
                    <td className="px-3 py-2 text-slate-600">{e.cc}</td>
                    <td className="px-3 py-2 text-slate-600">{e.portfolio}</td>
                    <td className="px-3 py-2 text-slate-600">{e.role || "—"}</td>
                    <td className="px-3 py-2 text-center">{pct(e.apr)}</td>
                    <td className="px-3 py-2 text-center">{pct(e.may)}</td>
                    <td className="px-3 py-2 text-center">{pct(e.jun)}</td>
                    <td className="px-3 py-2 text-center font-semibold">{pct(e.avg)}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${color}`}>{label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <EmployeeProjectsModal employee={selectedEmp} onClose={() => setSelectedEmp(null)} />
    </div>
  );
}
